/*
 *   File : manage-news.js
 *   Author : https://evoqins.com
 *   Description : Pause reward modal component
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { memo, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

// Custom component 
import { PrimaryButton, SecondaryButton, TextButton } from '../Buttons';
import { APIService, AWSService } from '../../Service';
import { toast } from 'react-toastify';
import { CustomEditor, CustomFileUpload, CustomSelectBox, CustomTextInput, TagSelector } from '../FormElements';

const TAGS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const ManageNewsModal = (props) => {

    const is_super_admin = Cookies.get("admin_type") === "1" ? true : false;

    const [title, setTitle] = useState("");
    const [titleError, setTitleError] = useState("");

    const [tagData, setTagData] = useState([]);
    const [tagDataLoading, setTagDataLoading] = useState(true);
    const [selectedTag, setSelectedTag] = useState(null);
    const [selectedTagError, setSelectedTagError] = useState("");

    const [editor, setEditor] = useState(EditorState.createEmpty());

    const [editorData, setEditorData] = useState(null);
    const [editorError, setEditorError] = useState("");

    const [image, setImage] = useState(null);
    const [imageError, setImageError] = useState("");
    const [imageLoader, setImageLoader] = useState(false);

    const [createLoader, setCreateLoader] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const [selectedCountries, setSelectedCountries] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [countryDataLoading, setCountryDataLoading] = useState(false);

    const [errorUpdate, setErrorUpdate] = useState(false);

    useEffect(() => {
        if (Object.keys(props.editData).length !== 0) {
            setTitle(props.editData.title);
            setImage(props.editData.image_url);
            setSelectedTag({ value: props.editData.tag, label: props.editData.tag_name });
            setEditorData(props.editData.content);

            const html_content = htmlToDraft(props.editData.content); //convert html to draft
            const content = ContentState.createFromBlockArray(html_content.contentBlocks);
            const updated_content = EditorState.createWithContent(content);

            setEditor(updated_content);

            if (is_super_admin) {
                const updated_selected_countries = props.editData.countries.map(country => {
                    return {
                        value: country.value,
                        label: country.label,
                    };
                });

                setSelectedCountries(updated_selected_countries);
            }


        }
    }, [props.editData]);

    useEffect(() => {
        _getNewsTag();
        if (is_super_admin === true) {
            _getCountries();
        }
    }, [is_super_admin]);

    useEffect(() => {
        setTitleError("");
    }, [title]);

    useEffect(() => {
        setEditorError("");
    }, [editorData]);

    useEffect(() => {
        setImageError("");
    }, [image]);

    useEffect(() => {
        setSelectedTagError("");
    }, [selectedTag]);


    // handle edit editor
    const _handleEditorChange = (data) => {
        setEditor(data);
        _getHtmlFormat();
    };

    // convert html format 
    const _getHtmlFormat = () => {
        const content = editor.getCurrentContent();
        const raw_content = convertToRaw(content);
        const html_content = draftToHtml(raw_content);

        setEditorData(html_content);
    };


    // handle select tag 
    const _handleSelectTag = (value) => {
        setSelectedTag(value);
    };

    // handle select countries
    const _handleSelectCountries = (value) => {
        setSelectedCountries(value);
    };

    // handle upload image
    const _handleUploadImage = (url) => {
        setImageLoader(true);
        _getPresignedUrl(url);
    };

    // handle change image
    const _handleChangeImage = () => {
        setImage(null);
    };

    // handle close modal
    const _handleCloseModal = () => {
        var button = document.getElementById("manage-news-modal-close");
        button.click();
        props.handleClose();
    };

    // handle create news
    const _handleCreateNews = () => {
        let is_valid = true;

        if (title.trim().length === 0) {
            setTitleError("Title is required");
            is_valid = false;
        }

        if (editorData === null || _validateEmptyTag(editorData, TAGS)) {
            setEditorError("Content is required. Remove whitespace if any.");
            is_valid = false;
        }

        if (selectedTag === null) {
            setSelectedTagError("Tag is required");
            is_valid = false;
        }

        if (image === null) {
            setImageError("Upload an image");
            is_valid = false;
        }

        setErrorUpdate(prev => !prev);

        if (is_valid === true) {
            if (props.editData?.news_id) {
                _updateNews();
            } else {
                _createNews();
            }
        }
    };

    // handle validate empty tag 
    const _validateEmptyTag = (html_value, tag_value) => {
        let element = document.createElement('div');
        element.innerHTML = html_value;

        for (let tag of tag_value) {
            let elements = element.getElementsByTagName(tag);
            for (let element of elements) {
                let innerHTML = element.innerHTML.trim();
                if (innerHTML === '' || innerHTML === '&nbsp;') {
                    return true;
                }
            }
        }
        return false;
    };

    // get presigned url
    async function _getPresignedUrl(file) {
        let is_post = true;
        let url = 'general/file-upload';
        let data = {
            "file": file.name,
            "is_public": true
        };
        try {
            let response = await APIService(is_post, url, data);

            if (response.status_code === 200) {
                let aws_fields = response.data.upload_info.fields;
                let aws_url = response.data.upload_info.url;

                try {
                    let aws_response = await AWSService(aws_fields, file, aws_url);
                    if (aws_response.status === 'ok') {
                        setImage(response.data.view_info);
                    } else {
                        toast.dismiss();
                        toast.error(aws_response.message, {
                            type: "error",
                            className: "e-toast",
                        });
                    }
                } catch (err) {
                    console.log("error")
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        } catch (err) {
        }
        setImageLoader(false);
    };

    // API - Create news 
    const _createNews = () => {
        setCreateLoader(true);

        const updated_selected_countries = selectedCountries.map(item => item.value);

        let is_post = true;
        let url = is_super_admin ? 'news/create' : 'CA/news/create';
        let data = {
            "title": title,
            "content": editorData,
            "tag": selectedTag?.value || null,
            "image_url": image,
            "countries": updated_selected_countries
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("News created successfully", {
                    type: "success",
                    className: "e-toast",
                });

                setTitle("");
                setEditorData(null);
                setEditor(EditorState.createEmpty());
                setSelectedTag(null);
                setImage(null);
                // setLoading(true);
                setSelectedCountries([]);

                // _getNewsList();
                props.handleSuccess();
                _handleCloseModal();

            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setCreateLoader(false);
        });
    };

    // API - update news 
    const _updateNews = () => {
        setCreateLoader(true);

        const updated_selected_countries = selectedCountries.map(item => item.value);

        let is_post = true;
        let url = is_super_admin ? 'news/update' : 'CA/news/update';
        let data = {
            "news_id": props.editData?.news_id,
            "title": title,
            "content": editorData,
            "tag": selectedTag?.value || null,
            "image_url": image,
            "countries": updated_selected_countries
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("News updated successfully", {
                    type: "success",
                    className: "e-toast",
                });

                setTitle("");
                setEditorData(null);
                setEditor(EditorState.createEmpty());
                setSelectedTag(null);
                setImage(null);
                // setNewsEditData({});
                // setLoading(true);
                setSelectedCountries([]);

                // _getNewsList();

                props.handleSuccess();
                _handleCloseModal();
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setCreateLoader(false);
        });
    };

    // API - Get news tag
    const _getNewsTag = () => {
        let is_post = false;
        let url = 'general/news-tag';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setTagData(response.data);
            } else {
            }
            setTagDataLoading(false);
        });
    };

    // API - Get countries list
    const _getCountries = () => {

        const country_filter_options = [];

        let is_post = false;
        let url = 'general/list-countries';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setCountryList(response.data);
            } else {
            }
            setCountryDataLoading(false);
        });
    };

    return (
        <div className="modal fade" id="manage-news-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="approveRequestModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered e-border-radius-16 modal-dialog-scrollable">
                <div className="modal-content e-border-radius-16">
                    <div className="modal-header px-4 pt-4 pb-2 border-0 align-items-start justify-content-between">
                        <h6 className='mb-0 e-primary-font-playfair e-font-18 e-text-raisin-black e-font-weight-500 line-height-28px'>
                            {props.title}
                        </h6>
                        <img src={require("../../Assets/Image/CountryAdmin/close-icon.svg").default}
                            draggable={false}
                            className='cursor-pointer'
                            alt='close'
                            onClick={_handleCloseModal} />
                        <button type="button" id='manage-news-modal-close' className="btn-close shadow-none d-none" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body px-4 pb-3 pt-0">

                        {/* Title */}
                        <CustomTextInput label="Title"
                            placeholder="News title"
                            type="text"
                            require
                            value={title}
                            error={titleError}
                            className="w-100"
                            labelStyle="e-font-weight-500 e-text-raisin-black"
                            handleChange={(e) => setTitle(e)} />

                        {/* custom wysiwyg editor */}
                        <CustomEditor label="Content"
                            editorState={editor}
                            require
                            editorStyle={{ minHeight: "200px" }}
                            options={['inline', 'blockType', 'list', 'link']}
                            className='h-296px'
                            error={editorError}
                            onEditorStateChange={_handleEditorChange} />

                        {/* tag selector */}
                        <CustomSelectBox label="Add tag"
                            value={selectedTag}
                            options={tagData}
                            error={selectedTagError}
                            menuIsOpen={menuOpen}
                            menuPortalTarget={document.body}
                            className="e-section-select e-bg-white"
                            isSearchable={false}
                            onMenuOpen={() => setMenuOpen(true)}
                            onMenuClose={() => setMenuOpen(false)}
                            selectChange={(id) => _handleSelectTag(id)}
                        />

                        {/* countries select */}
                        {is_super_admin &&
                            <TagSelector label="Select countries"
                                value={selectedCountries}
                                options={countryList}
                                menuPortalTarget={document.body}
                                className="e-bg-white e-border-radius-12"
                                handleTagChange={_handleSelectCountries} />
                        }

                        {/* upload image */}
                        <CustomFileUpload label="Upload thumbnail"
                            placeholder="Drop your image here"
                            file={image}
                            fileType={["JPG", "PNG", "JPEG"]}
                            resolution="328px * 90px"
                            requiredWidth={328}
                            requiredHeight={90}
                            errorUpdate={errorUpdate}
                            require
                            error={imageError}
                            loader={imageLoader}
                            handleChange={_handleUploadImage} />

                        {/* change thumbnail button */}
                        {image !== null &&
                            <div className='mb-2 margin-neg-20px-top'>
                                <TextButton name="Change image"
                                    onPress={_handleChangeImage} />
                            </div>
                        }

                    </div>

                    <div className='modal-footer border-0 px-4 pb-4 pt-2'>
                        <div className='row g-3 w-100'>
                            <div className='col-6 ps-0'>
                                <SecondaryButton name="Cancel"
                                    className="w-100"
                                    onPress={_handleCloseModal} />
                            </div>
                            <div className='col-6 pe-0'>
                                <PrimaryButton name={props.primaryButtonLabel}
                                    className="w-100"
                                    status={createLoader}
                                    onPress={_handleCreateNews} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default memo(ManageNewsModal);